/**
 * Singleton WebSocket to the CEX control-plane (`/api/ws`) that fans out
 * **ambient** subjects — events every authenticated client receives without
 * having to `subscribe`. See `app/api/ws.py::AMBIENT_SUBJECTS`.
 *
 * Why not reuse `accountOrderStreamHub.js`? That hub opens one socket *per*
 * accountId and explicitly subscribes to `order.<id>.update`. For token-group
 * change notifications we need exactly one connection for the whole tab,
 * regardless of how many accounts the user has.
 *
 * Typical subjects: `cex.token_groups.changed`, `cex.bots.changed` (see control-plane `ws.AMBIENT_SUBJECTS`).
 *
 * Public API:
 *     subscribeAmbient(subject, handler) → unsubscribe
 *
 * The handler receives the decoded `data` payload. Multiple subscribers for
 * the same subject are supported; the hub reference-counts and tears the
 * socket down when the last listener unsubscribes.
 */
import { cexWebSocketUrl } from '../lib/apiBase.js'

const CLOSE_POLICY_VIOLATION = 1008

/** @type {WebSocket | null} */
let socket = null
let reconnectTimer = null
let retryMs = 1000
/** @type {Map<string, Set<(data: unknown) => void>>} */
const listeners = new Map()

function totalListeners() {
  let n = 0
  for (const s of listeners.values()) n += s.size
  return n
}

function openSocket() {
  if (totalListeners() === 0) return
  if (socket) {
    const st = socket.readyState
    if (st === WebSocket.OPEN || st === WebSocket.CONNECTING) return
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  let ws
  try {
    ws = new WebSocket(cexWebSocketUrl())
  } catch {
    scheduleReconnect()
    return
  }
  socket = ws
  ws.onopen = () => {
    retryMs = 1000
    // Other tabs / browsers do not get missed NATS until reconnect — let CexContext
    // refetch once the socket is (re)established.
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      try {
        window.dispatchEvent(new CustomEvent('lotos:cex-ambient-ws-open'))
      } catch {
        /* ignore */
      }
    }
  }
  ws.onmessage = (ev) => {
    let msg
    try {
      msg = JSON.parse(ev.data)
    } catch {
      return
    }
    if (!msg || typeof msg.subject !== 'string') return
    const set = listeners.get(msg.subject)
    if (!set || set.size === 0) return
    set.forEach((fn) => {
      try {
        fn(msg.data)
      } catch {
        /* ignore */
      }
    })
  }
  ws.onclose = (ev) => {
    socket = null
    if (ev?.code === CLOSE_POLICY_VIOLATION) return
    if (totalListeners() > 0) scheduleReconnect()
  }
  ws.onerror = () => {
    try {
      ws.close()
    } catch {
      /* ignore */
    }
  }
}

function scheduleReconnect() {
  if (reconnectTimer) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    openSocket()
  }, retryMs)
  retryMs = Math.min(retryMs * 2, 30_000)
}

function maybeTearDown() {
  if (totalListeners() > 0) return
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (socket) {
    try {
      socket.close()
    } catch {
      /* ignore */
    }
    socket = null
  }
}

/**
 * Subscribe to an ambient subject (e.g. `cex.token_groups.changed`).
 *
 * @param {string} subject
 * @param {(data: unknown) => void} handler
 * @returns {() => void} unsubscribe
 */
export function subscribeAmbient(subject, handler) {
  if (!subject || typeof handler !== 'function') return () => {}
  let set = listeners.get(subject)
  if (!set) {
    set = new Set()
    listeners.set(subject, set)
  }
  set.add(handler)
  openSocket()
  return () => {
    const s = listeners.get(subject)
    if (!s) return
    s.delete(handler)
    if (s.size === 0) listeners.delete(subject)
    maybeTearDown()
  }
}
